import { Component } from "react";
import PropTypes from "prop-types";

export default class Shortcut extends Component {
  static propTypes = {
    videoActions: PropTypes.object.isRequired,
    videoState: PropTypes.object.isRequired,
    onSeekChange: PropTypes.func,
    onVolumeChange: PropTypes.func,
    onKeyboardSeekStart: PropTypes.func,
    onKeyboardSeekChange: PropTypes.func,
    onKeyboardSeekEnd: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.repeatInterval = null;
    this.repeatTimeout = null;
    this.seekAmount = 0;
    this.seekTimeout = null;

    // Keyboard seeking state
    this.isKeyboardSeeking = false;
    this.keyboardSeekStartTime = 0;
    this.keyboardSeekTargetTime = 0;
    this.keyboardSeekTimeout = null;

    this.shortcuts = [
      {
        keyCode: 32, // spacebar
        handle: this.togglePlay,
      },
      {
        keyCode: 75, // k
        handle: this.togglePlay,
      },
      {
        keyCode: 37, // Left arrow
        handle: this.handleSeekLeft,
      },
      {
        keyCode: 74, // j
        handle: this.handleSeekLeft,
      },
      {
        keyCode: 39, // Right arrow
        handle: this.handleSeekRight,
      },
      {
        keyCode: 76, // l
        handle: this.handleSeekRight,
      },
      {
        keyCode: 38, // Up arrow
        handle: this.handleVolumeUp,
      },
      {
        keyCode: 40, // Down arrow
        handle: this.handleVolumeDown,
      },
    ];
  }

  componentDidMount() {
    document.addEventListener("keydown", this.handleKeyPress);
    document.addEventListener("keyup", this.handleKeyUp);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleKeyPress);
    document.removeEventListener("keyup", this.handleKeyUp);
    this.clearRepeatInterval();
    this.clearSeekTimeout();
    this.clearKeyboardSeekTimeout();
  }

  clearRepeatInterval = () => {
    if (this.repeatTimeout) {
      clearTimeout(this.repeatTimeout);
      this.repeatTimeout = null;
    }
    if (this.repeatInterval) {
      clearInterval(this.repeatInterval);
      this.repeatInterval = null;
    }
  };

  clearSeekTimeout = () => {
    if (this.seekTimeout) {
      clearTimeout(this.seekTimeout);
      this.seekTimeout = null;
    }
  };

  clearKeyboardSeekTimeout = () => {
    if (this.keyboardSeekTimeout) {
      clearTimeout(this.keyboardSeekTimeout);
      this.keyboardSeekTimeout = null;
    }
  };

  handleSeek = (amount) => {
    const {
      videoActions,
      videoState,
      onSeekChange,
      onKeyboardSeekStart,
      onKeyboardSeekChange,
      onKeyboardSeekEnd,
    } = this.props;

    // Если это первое нажатие - начинаем keyboard seeking
    if (!this.isKeyboardSeeking) {
      this.isKeyboardSeeking = true;
      this.keyboardSeekStartTime = videoState.currentTime;
      this.seekAmount = 0; // Сбрасываем накопление для индикатора

      if (onKeyboardSeekStart) {
        onKeyboardSeekStart(videoState.currentTime);
      }
    }

    // Накапливаем значение для индикатора
    this.seekAmount += amount;

    // Вычисляем целевую позицию
    this.keyboardSeekTargetTime = Math.max(
      0,
      Math.min(
        videoState.duration || Infinity,
        this.keyboardSeekStartTime + this.seekAmount
      )
    );

    // Уведомляем о изменении для визуального обновления seekbar
    if (onKeyboardSeekChange) {
      onKeyboardSeekChange(this.keyboardSeekTargetTime);
    }

    // Уведомляем о изменении для индикатора
    if (onSeekChange) {
      onSeekChange(this.seekAmount);
    }

    // Сбрасываем таймауты
    this.clearKeyboardSeekTimeout();
    this.clearSeekTimeout();

    // Применяем финальный seek через 1 секунду
    this.keyboardSeekTimeout = setTimeout(() => {
      videoActions.navigate(this.keyboardSeekTargetTime);
      this.isKeyboardSeeking = false;

      if (onKeyboardSeekEnd) {
        onKeyboardSeekEnd(this.keyboardSeekTargetTime);
      }
    }, 1000);

    // Скрываем индикатор через 1.5 секунды (после применения seek)
    this.seekTimeout = setTimeout(() => {
      this.seekAmount = 0;
      if (onSeekChange) {
        onSeekChange(0);
      }
    }, 1500);
  };

  handleSeekLeft = () => {
    this.handleSeek(-5);
  };

  handleSeekRight = () => {
    this.handleSeek(5);
  };

  handleVolumeChange = (delta) => {
    const { videoActions, videoState, onVolumeChange } = this.props;
    const currentVolume = videoState.muted ? 0 : videoState.volume || 0;
    const newVolume = Math.max(0, Math.min(1, currentVolume + delta));

    videoActions.setVolume(newVolume);

    // Если был muted и увеличиваем громкость, размутить
    if (videoState.muted && delta > 0) {
      videoActions.unmute();
    }

    // Уведомляем о изменении громкости
    if (onVolumeChange) {
      onVolumeChange(newVolume);
    }
  };

  handleVolumeUp = () => {
    this.handleVolumeChange(0.1);
  };

  handleVolumeDown = () => {
    this.handleVolumeChange(-0.1);
  };

  togglePlay = () => {
    this.props.videoActions.togglePlayUser();
  };

  handleKeyPress = (e) => {
    // Блокируем горячие клавиши только если фокус на элементах ввода
    const activeElement = document.activeElement;
    if (activeElement) {
      const tagName = activeElement.nodeName;
      const isEditable = activeElement.isContentEditable;

      // Пропускаем обработку только для полей ввода
      if (
        tagName === "INPUT" ||
        tagName === "TEXTAREA" ||
        tagName === "SELECT" ||
        isEditable
      ) {
        return false;
      }
    }

    const keyCode = e.keyCode || e.which;
    const ctrl = e.ctrlKey || e.metaKey;
    const shift = e.shiftKey;
    const alt = e.altKey;

    // Обработка удержания клавиш для перемотки и громкости (все стрелки)
    if ([37, 38, 39, 40].includes(keyCode)) {
      e.preventDefault(); // Всегда предотвращаем действие по умолчанию для стрелок

      if (!this.repeatInterval) {
        // Первое нажатие - выполняем сразу
        const shortcut = this.shortcuts.find((s) => s.keyCode === keyCode);
        if (shortcut) {
          shortcut.handle();
        }

        // Начинаем повторение через 300ms
        this.repeatTimeout = setTimeout(() => {
          this.repeatInterval = setInterval(() => {
            const shortcut = this.shortcuts.find((s) => s.keyCode === keyCode);
            if (shortcut) {
              shortcut.handle();
            }
          }, 200); // повторять каждые 200ms
        }, 300);
      }
      return;
    }

    // Обработка остальных горячих клавиш
    const shortcut = this.shortcuts.filter((s) => {
      if (s.keyCode !== keyCode) {
        return false;
      }
      if (
        (s.ctrl !== undefined && s.ctrl !== ctrl) ||
        (s.shift !== undefined && s.shift !== shift) ||
        (s.alt !== undefined && s.alt !== alt)
      ) {
        return false;
      }
      return true;
    })[0];

    if (shortcut) {
      shortcut.handle();
      e.preventDefault();
    }
  };

  handleKeyUp = (e) => {
    const keyCode = e.keyCode || e.which;

    // Останавливаем повторение при отпускании любых стрелок
    if ([37, 38, 39, 40].includes(keyCode)) {
      this.clearRepeatInterval();
    }
  };

  render() {
    return null;
  }
}
